import { ChangeEvent, SyntheticEvent, useContext, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import commonStyles from "../common.module.css";
import { useRegistration } from "../../api/queries/auth/useRegistration";
import { NavLink, useNavigate } from "react-router-dom";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { FormErrors } from "../../api/types/base";
import { appToastContext } from "../../context/toast";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";

export default function AuthRegistration() {
  const navigate = useNavigate();
  const registration = useRegistration();
  const { data: journeyTypes } = useJourneyTypes();
  const { ref: appToastRef } = useContext(appToastContext);

  const [email, setEmail] = useState<string>("");
  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value);

  const [password, setPassword] = useState<string>("");
  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) =>
    setPassword(event.target.value);

  const [passwordConfirm, setPasswordConfirm] = useState<string>("");
  const onPasswordConfirmChange = (event: ChangeEvent<HTMLInputElement>) =>
    setPasswordConfirm(event.target.value);

  const [context, setContext] = useState<string>("");
  const onContextChange = (event: DropdownChangeEvent) =>
    setContext(event.value);

  const [errors, setErrors] = useState<FormErrors<FormValue>>();
  const validate = (): boolean => {
    let newErrors: FormErrors<FormValue> = {};

    if (!email) {
      newErrors.email = "";
    }

    if (!password) {
      newErrors.password = "";
    }

    if (!passwordConfirm) {
      newErrors.passwordConfirm = "";
    }

    if (password !== passwordConfirm) {
      newErrors.password = "";
      newErrors.passwordConfirm = "";
    }

    if (!context) {
      newErrors.context = "";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    await registration.mutateAsync({
      email,
      password,
      password_confirm: passwordConfirm,
      context,
    });

    appToastRef?.current?.show({
      severity: "success",
      summary: "The registration was successful.",
    });

    navigate("/login");
  };

  const footer = () => (
    <div className="text-center">
      <small>
        Have an account? <NavLink to={"/login"}>Sign in</NavLink>.
      </small>
    </div>
  );

  return (
    <form className={"col-12 p-fluid"} onSubmit={handleSubmit}>
      <Card
        title={"Registration"}
        className={"col-12 md:col-4 md:col-offset-4"}
        footer={footer}
      >
        <div className={"field"}>
          <label className={commonStyles.labelRequired}>E-mail:</label>
          <InputText
            value={email}
            onChange={onEmailChange}
            className={classNames({
              "p-invalid": errors?.hasOwnProperty("email"),
            })}
          />
        </div>

        <div className={"field"}>
          <label className={commonStyles.labelRequired}>Password:</label>
          <InputText
            type={"password"}
            value={password}
            onChange={onPasswordChange}
            className={classNames({
              "p-invalid": errors?.hasOwnProperty("password"),
            })}
          />
        </div>

        <div className={"field"}>
          <label className={commonStyles.labelRequired}>
            Password confirm:
          </label>
          <InputText
            type={"password"}
            value={passwordConfirm}
            onChange={onPasswordConfirmChange}
            className={classNames({
              "p-invalid": errors?.hasOwnProperty("passwordConfirm"),
            })}
          />
        </div>

        <div className="field">
          <label className={commonStyles.labelRequired}>Context:</label>
          <Dropdown
            options={journeyTypes?.data.results || []}
            value={context}
            optionLabel="name"
            optionValue="id"
            onChange={onContextChange}
            className={classNames({
              "p-invalid": errors?.hasOwnProperty("context"),
            })}
          />
        </div>

        <Button label={"Register"} disabled={registration.isLoading} />
      </Card>
    </form>
  );
}

interface FormValue {
  email: string;
  password: string;
  passwordConfirm: string;
  context: string;
}
